import { useState, useMemo, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { type ColumnDef, type SortingState, type RowSelectionState } from "@tanstack/react-table"
import { Plus, Search, Building2, Upload } from "lucide-react"
import { useCompanies } from "@/hooks/use-companies"
import { useCustomFields } from "@/hooks/use-custom-fields"
import { useCustomFieldColumns } from "@/hooks/use-custom-field-columns"
import { useColumnPreferences } from "@/hooks/use-column-preferences"
import { useBulkDelete, useBulkUpdate, useBulkTag } from "@/hooks/use-bulk-actions"
import { useFilterParams } from "@/hooks/use-filter-params"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/data-table"
import { ColumnSelector, type ColumnOption } from "@/components/column-selector"
import { SortableHeader } from "@/components/sortable-header"
import { EmptyState } from "@/components/empty-state"
import { Badge } from "@/components/ui/badge"
import { BulkActionBar, buildDeleteAction, buildTagAction, buildAssignAction } from "@/components/bulk-action-bar"
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog"
import { BulkAssignDialog } from "@/components/bulk-assign-dialog"
import { BulkTagDialog } from "@/components/bulk-tag-dialog"
import { FilterPanel, SelectFilter, TextFilter, DateRangeFilter } from "@/components/filters"
import { ImportWizard } from "@/components/import-wizard"
import { ExportButton } from "@/components/export-button"
import { createSelectColumn } from "@/lib/bulk-selection"
import { LoadMoreButton } from "@/components/load-more-button"
import { formatDate, sortingStateToParam, buildDetailUrl } from "@/lib/utils"
import { toast } from "sonner"
import type { Company } from "@/lib/types"

const COMPANY_FILTER_KEYS = [
  "status_id", "industry_id", "acquisition_source_id", "owner_id", "city",
  "created_at_from", "created_at_to",
] as const

const BASE_COLUMNS: ColumnDef<Company, unknown>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column}>Nazwa</SortableHeader>,
    cell: ({ row }) => (
      <div>
        <div className="font-medium text-foreground">{row.original.name}</div>
        {row.original.short_name && (
          <div className="text-xs text-muted-foreground">
            {row.original.short_name}
          </div>
        )}
      </div>
    ),
  },
  {
    accessorKey: "nip",
    header: ({ column }) => <SortableHeader column={column}>NIP</SortableHeader>,
    cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.nip || "\u2014"}</span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.status ? (
        <Badge variant="outline" className="text-xs">
          {row.original.status.label}
        </Badge>
      ) : (
        <span className="text-muted-foreground">{"\u2014"}</span>
      ),
  },
  {
    accessorKey: "owner",
    header: "Opiekun",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.owner ? (
        <span className="text-sm">
          {row.original.owner.first_name} {row.original.owner.last_name}
        </span>
      ) : (
        <span className="text-muted-foreground">{"\u2014"}</span>
      ),
  },
  {
    accessorKey: "city",
    header: ({ column }) => <SortableHeader column={column}>Miasto</SortableHeader>,
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.city || "\u2014"}</span>,
  },
  {
    accessorKey: "email",
    header: ({ column }) => <SortableHeader column={column}>Email</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-sm">{row.original.email || "\u2014"}</span>
    ),
  },
  {
    accessorKey: "phone",
    header: ({ column }) => <SortableHeader column={column}>Telefon</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-sm">{row.original.phone || "\u2014"}</span>
    ),
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => <SortableHeader column={column}>Utworzono</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-xs text-muted-foreground">
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
]

const DEFAULT_VISIBLE = ["name", "nip", "status", "owner", "city", "email", "phone", "created_at"]

const BASE_COLUMN_LABELS: Record<string, string> = {
  name: "Nazwa",
  nip: "NIP",
  status: "Status",
  owner: "Opiekun",
  city: "Miasto",
  email: "Email",
  phone: "Telefon",
  created_at: "Utworzono",
}

export function CompaniesListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [showTagDialog, setShowTagDialog] = useState(false)
  const [showImportWizard, setShowImportWizard] = useState(false)
  const [cursor, setCursor] = useState<string | undefined>()
  const [allCompanies, setAllCompanies] = useState<Company[]>([])
  const prevKeyRef = useRef("")

  const { filters, setFilter, clearFilters, activeFilterCount } = useFilterParams(COMPANY_FILTER_KEYS)

  const sortParam = sortingStateToParam(sorting)
  const { data, isLoading, isFetching } = useCompanies({
    search: debouncedSearch,
    sort: sortParam,
    cursor,
    ...filters,
  })

  // Reset cursor when filters/sort/search change
  const filterKey = JSON.stringify({ search: debouncedSearch, sort: sortParam, ...filters })
  useEffect(() => {
    if (filterKey !== prevKeyRef.current) {
      prevKeyRef.current = filterKey
      setCursor(undefined)
      setAllCompanies([])
    }
  }, [filterKey])

  // Accumulate results
  useEffect(() => {
    if (data?.data) {
      if (cursor) {
        setAllCompanies((prev) => [...prev, ...data.data])
      } else {
        setAllCompanies(data.data)
      }
    }
  }, [data])

  const bulkDeleteMutation = useBulkDelete("companies")
  const bulkUpdateMutation = useBulkUpdate("companies")
  const bulkTagMutation = useBulkTag("companies")

  // Custom fields
  const { data: cfData } = useCustomFields("companies")
  const customFields = cfData?.data
  const customFieldCols = useCustomFieldColumns<Company>(customFields)

  // Merge all columns with select column
  const allColumns = useMemo(
    () => [createSelectColumn<Company>(), ...BASE_COLUMNS, ...customFieldCols] as ColumnDef<Company, unknown>[],
    [customFieldCols]
  )

  // Build column IDs and options for selector
  const allColumnIds = useMemo(() => {
    const baseIds = BASE_COLUMNS.map(
      (c) => (c as { accessorKey?: string }).accessorKey ?? (c as { id?: string }).id ?? ""
    )
    const cfIds = customFieldCols.map((c) => (c as { id?: string }).id ?? "")
    return [...baseIds, ...cfIds]
  }, [customFieldCols])

  const columnOptions: ColumnOption[] = useMemo(() => {
    const base = Object.keys(BASE_COLUMN_LABELS).map((id) => ({
      id,
      label: BASE_COLUMN_LABELS[id],
    }))
    const cf = (customFields ?? []).map((f) => ({
      id: `cf_${f.key}`,
      label: f.label?.pl || f.label?.en || f.key,
      isCustomField: true,
    }))
    return [...base, ...cf]
  }, [customFields])

  const {
    columnVisibility,
    setColumnVisibility,
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    isColumnVisible,
  } = useColumnPreferences("companies", allColumnIds, DEFAULT_VISIBLE)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    clearTimeout((window as unknown as Record<string, unknown>).__searchTimeout as number)
    ;(window as unknown as Record<string, unknown>).__searchTimeout = setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
  }

  const companies = allCompanies
  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k])
  const selectedCount = selectedIds.length

  const handleBulkDelete = async () => {
    const result = await bulkDeleteMutation.mutateAsync({ ids: selectedIds })
    toast.success(`Usunieto ${result.deleted} kontrahentow`)
    setRowSelection({})
  }

  const handleBulkAssign = async (userId: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { owner_id: userId },
    })
    toast.success(`Zaktualizowano ${result.updated} kontrahentow`)
    setRowSelection({})
  }

  const handleBulkTag = async (tagNames: string[]) => {
    const result = await bulkTagMutation.mutateAsync({
      ids: selectedIds,
      tag_names: tagNames,
    })
    toast.success(`Otagowano ${result.tagged} kontrahentow`)
    setRowSelection({})
  }

  const bulkActions = [
    buildAssignAction(() => setShowAssignDialog(true), "Przypisz opiekuna"),
    buildTagAction(() => setShowTagDialog(true)),
    buildDeleteAction(() => setShowDeleteDialog(true)),
  ]

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={20} className="text-primary" />
          <h1 className="page-title">
            {t("companies.title")}
          </h1>
          {data && (
            <span className="text-sm text-muted-foreground">
              ({data.data.length}{data.meta.has_more ? "+" : ""})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            entity="companies"
            filters={{ search: debouncedSearch, ...filters }}
            visibleColumns={Object.keys(columnVisibility).filter((k) => columnVisibility[k] !== false)}
          />
          <Button variant="outline" onClick={() => setShowImportWizard(true)}>
            <Upload size={16} />
            Importuj CSV
          </Button>
          <Button onClick={() => navigate("/companies/new")}>
            <Plus size={16} />
            {t("companies.new")}
          </Button>
        </div>
      </div>

      {/* Search + Column selector */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t("common.search")}
            className="h-9 w-full rounded-lg border border-input bg-transparent pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ColumnSelector
          columns={columnOptions}
          visibleCount={visibleCount}
          totalCount={totalCount}
          isColumnVisible={isColumnVisible}
          onToggleColumn={toggleColumn}
          onShowAll={showAll}
          onHideAll={hideAll}
        />
      </div>

      {/* Filters */}
      <FilterPanel activeCount={activeFilterCount} onClear={clearFilters}>
        <SelectFilter
          label="Status"
          value={filters.status_id || ""}
          onChange={(v) => setFilter("status_id", v)}
          options={[]}
          placeholder="Wszystkie statusy"
        />
        <SelectFilter
          label="Branza"
          value={filters.industry_id || ""}
          onChange={(v) => setFilter("industry_id", v)}
          options={[]}
          placeholder="Wszystkie branze"
        />
        <SelectFilter
          label="Zrodlo pozyskania"
          value={filters.acquisition_source_id || ""}
          onChange={(v) => setFilter("acquisition_source_id", v)}
          options={[]}
          placeholder="Wszystkie zrodla"
        />
        <TextFilter
          label="Miasto"
          value={filters.city || ""}
          onChange={(v) => setFilter("city", v)}
          placeholder="Wpisz nazwe miasta"
        />
        <DateRangeFilter
          label="Data utworzenia"
          fromValue={filters.created_at_from || ""}
          toValue={filters.created_at_to || ""}
          onFromChange={(v) => setFilter("created_at_from", v)}
          onToChange={(v) => setFilter("created_at_to", v)}
        />
      </FilterPanel>

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedCount}
        onDeselectAll={() => setRowSelection({})}
        actions={bulkActions}
      />

      {/* Table / Empty state */}
      {!isLoading && !debouncedSearch && data && data.data.length === 0 ? (
        <EmptyState
          icon={Building2}
          heading="Brak kontrahentow"
          description="Dodaj swojego pierwszego kontrahenta"
          actionLabel="Nowy kontrahent"
          onAction={() => navigate("/companies/new")}
        />
      ) : (
        <DataTable
          columns={allColumns}
          data={companies}
          isLoading={isLoading}
          onRowClick={(company) => navigate(buildDetailUrl("/companies", company.id, { sort: sortingStateToParam(sorting), filters: filters as Record<string, string | undefined> }))}
          emptyMessage={t("common.noResults")}
          sorting={sorting}
          onSortingChange={setSorting}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          getRowId={(row) => row.id}
        />
      )}

      {/* Load more */}
      <LoadMoreButton
        hasMore={data?.meta.has_more ?? false}
        isLoading={isFetching && !!cursor}
        onClick={() => {
          if (data?.meta.next_cursor) setCursor(data.meta.next_cursor)
        }}
      />

      {/* Bulk action dialogs */}
      <BulkDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        count={selectedCount}
        entityLabel="kontrahentow"
        onConfirm={handleBulkDelete}
      />
      <BulkAssignDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        count={selectedCount}
        onConfirm={handleBulkAssign}
        fieldLabel="Opiekun"
      />
      <BulkTagDialog
        open={showTagDialog}
        onOpenChange={setShowTagDialog}
        count={selectedCount}
        onConfirm={handleBulkTag}
      />
      <ImportWizard
        open={showImportWizard}
        onOpenChange={setShowImportWizard}
        defaultEntityType="companies"
      />
    </div>
  )
}
